import { useEffect, useState } from "react";
import { fetchTrucks, fetchJobs, createTruck, updateTruck, updateTruckStatus, deleteTruck, sendTestPush } from "../lib/api";
import { reportTestResult } from "../components/PushTestButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Plus, Wrench, Trash2, Lock, Satellite, BellRing } from "lucide-react";
import { toast } from "sonner";
import { TRUCK_STATUS_COLORS, DEFAULT_CENTER } from "../lib/constants";
import { cn } from "../lib/utils";
import { useAuth } from "../lib/auth";
import GpsHealthBadge from "../components/GpsHealthBadge";

export default function FleetPage() {
  const { isAdmin, isStaff } = useAuth();
  const [trucks, setTrucks] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    driver: "",
    phone: "",
    status: "available",
    capacity: "Standard Tow",
    lat: DEFAULT_CENTER[0],
    lng: DEFAULT_CENTER[1],
  });

  const refresh = async () => {
    const [t, j] = await Promise.all([fetchTrucks(), fetchJobs()]);
    setTrucks(t);
    setJobs(j);
  };
  useEffect(() => {
    refresh();
  }, []);
  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", driver: "", phone: "", status: "available", capacity: "Standard Tow", lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] });
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name,
      driver: t.driver,
      phone: t.phone || "",
      status: t.status,
      capacity: t.capacity || "Standard Tow",
      lat: t.lat,
      lng: t.lng,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.driver) {
      toast.error("Truck name and driver required");
      return;
    }
    const payload = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng) };
    try {
      if (editing) {
        await updateTruck(editing.id, payload);
        toast.success("Truck updated");
      } else {
        await createTruck(payload);
        toast.success("Truck added");
      }
      setOpen(false);
      refresh();
    } catch {
      toast.error("Save failed");
    }
  };

  const setStatus = async (truck, status) => {
    if (truck.status === status) return; // no-op click on the active pill
    try {
      await updateTruckStatus(truck.id, status);
      refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403) {
        toast.error("You don't have permission to change truck status.");
      } else {
        toast.error(detail || `Couldn't set ${truck.name} to ${status}.`);
      }
    }
  };

  const remove = async (truck) => {
    const activeJob = jobs.find(
      (j) => j.assigned_truck_id === truck.id && !["completed", "cancelled"].includes(j.status)
    );
    if (activeJob) {
      toast.error(`Cannot remove ${truck.name} — currently on job for ${activeJob.customer_name}. Reassign or complete it first.`);
      return;
    }
    try {
      await deleteTruck(truck.id);
      toast.success(`${truck.name} removed from fleet`);
      refresh();
    } catch {
      toast.error("Failed to remove truck");
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="fleet-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Fleet</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your tow trucks and driver availability.</p>
        </div>
        <div className="flex items-center gap-3">
          <GpsHealthBadge />
          <Button
            onClick={openCreate}
            className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white disabled:bg-slate-300 disabled:cursor-not-allowed"
            data-testid="fleet-add-btn"
            disabled={!isAdmin}
            title={isAdmin ? "" : "Admin only"}
          >
            {isAdmin ? <Plus className="h-4 w-4.5" /> : <Lock className="h-4 w-4.5" />}
            Add Truck
          </Button>
        </div>
      </div>

      {!isAdmin && (
        <div
          className="bg-blue-50 border border-blue-200 rounded-md px-4 py-2.5 text-sm text-blue-800 flex items-center gap-2"
          data-testid="fleet-admin-notice"
        >
          <Lock className="h-4 w-4 shrink-0" />
          You&apos;re signed in as a Dispatcher. Adding or removing trucks is restricted to Admin users.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fleet-grid">
        {trucks.map((t) => (
          <div key={t.id} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`fleet-item-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-md bg-slate-900 flex items-center justify-center">
                  <Wrench className="h-5 w-5 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-display text-lg font-bold flex items-center gap-1.5">
                    {t.name}
                    {t.external_source === "telematics_guru" && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded"
                        title="Live GPS from Telematics Guru"
                        data-testid={`truck-gps-badge-${t.id}`}
                      >
                        <Satellite className="h-2.5 w-2.5" /> GPS
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-600">{t.driver || "—"}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{t.phone || "No phone"} · {t.capacity}</div>
                </div>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {["available", "busy", "offline"].map((s) => {
                const c = TRUCK_STATUS_COLORS[s];
                const isActive = t.status === s;
                return (
                  <button
                    key={s}
                    onClick={() => setStatus(t, s)}
                    disabled={!isStaff}
                    className={cn(
                      "px-2.5 py-1 rounded text-[11px] font-bold uppercase tracking-wider transition-all",
                      isActive ? cn(c.bg, c.text) : "bg-slate-50 text-slate-400 hover:bg-slate-100",
                      !isStaff && "opacity-60 cursor-not-allowed hover:bg-slate-50",
                    )}
                    title={isStaff ? "" : "Staff only"}
                    data-testid={`fleet-set-${s}-${t.id}`}
                  >
                    {s}
                  </button>
                );
              })}
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto text-emerald-700 hover:text-emerald-800"
                onClick={async () => {
                  try { reportTestResult(await sendTestPush({ truck_id: t.id })); }
                  catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send test alert"); }
                }}
                title="Send a test job alert to every phone signed in to this truck"
                data-testid={`fleet-test-alert-${t.id}`}
              >
                <BellRing className="h-4 w-4 mr-1" /> Test alert
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-blue-600 hover:text-blue-700"
                onClick={() => openEdit(t)}
                data-testid={`fleet-edit-${t.id}`}
              >
                Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-500 hover:text-rose-700 hover:bg-rose-50 disabled:opacity-40 disabled:cursor-not-allowed px-2"
                    data-testid={`fleet-remove-btn-${t.id}`}
                    aria-label={`Remove ${t.name}`}
                    disabled={!isAdmin}
                    title={isAdmin ? "Remove truck" : "Admin only"}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid={`fleet-remove-confirm-${t.id}`}>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-display font-black tracking-tight">
                      Remove {t.name}?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently remove <span className="font-semibold">{t.name}</span> ({t.driver}) from the fleet.
                      Historical jobs assigned to this truck remain in the database.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`fleet-remove-cancel-${t.id}`}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => remove(t)}
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                      data-testid={`fleet-remove-confirm-action-${t.id}`}
                    >
                      Remove Truck
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        {trucks.length === 0 && (
          <div className="col-span-full bg-white border border-dashed border-slate-300 rounded-md p-8 text-center text-sm text-slate-500">
            No trucks in fleet. Click &ldquo;Add Truck&rdquo; to add your first one.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="fleet-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black">
              {editing ? "Edit Truck" : "Add Truck"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="uppercase-label">Truck Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Truck 06" data-testid="fleet-input-name" />
            </div>
            <div>
              <Label className="uppercase-label">Driver</Label>
              <Input value={form.driver} onChange={(e) => setForm({ ...form, driver: e.target.value })} data-testid="fleet-input-driver" />
            </div>
            <div>
              <Label className="uppercase-label">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="fleet-input-phone" />
            </div>
            <div>
              <Label className="uppercase-label">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="fleet-select-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="busy">Busy</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="uppercase-label">Capacity</Label>
              <Select value={form.capacity} onValueChange={(v) => setForm({ ...form, capacity: v })}>
                <SelectTrigger data-testid="fleet-select-capacity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Standard Tow">Standard Tow</SelectItem>
                  <SelectItem value="Heavy Duty">Heavy Duty</SelectItem>
                  <SelectItem value="Flatbed">Flatbed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="uppercase-label">Lat</Label>
              <Input type="number" step="0.0001" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="fleet-input-lat" />
            </div>
            <div>
              <Label className="uppercase-label">Lng</Label>
              <Input type="number" step="0.0001" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="fleet-input-lng" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="fleet-cancel-btn">Cancel</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="fleet-save-btn">
              {editing ? "Save Changes" : "Add Truck"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
